import { useState, useEffect } from 'react';
import { Container, Header, Content, Navbar, Table, Button, Panel, Input, InputGroup, Message, Tag } from 'rsuite';
import { Search } from '@rsuite/icons';
import { getApiUrl, getAuthHeaders } from './utils/apiConfig';
import { useAuth } from './utils/useAuth';
import CopyButton from './components/CopyButton';
import InstanceDetailsDialog from './components/InstanceDetailsDialog';

const { Column, HeaderCell, Cell } = Table;

const formatMemory = (mb) => {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

const formatPrice = (price) => {
  if (!price) return '-';
  return `$${parseFloat(price).toFixed(2)}`;
};

// Normalize the API `available` field to "yes" | "no" | "maybe".
// Accepts the new string form and legacy booleans for backward compatibility.
const availabilityValue = (available) => {
  if (typeof available === 'string') {
    const v = available.toLowerCase();
    return v === 'maybe' || v === 'yes' || v === 'no' ? v : (v ? 'yes' : 'no');
  }
  return available ? 'yes' : 'no';
};

function GPUInstances() {
  const auth = useAuth();
  const [droplets, setDroplets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailsInstance, setDetailsInstance] = useState(null);

  // Trigger refresh when auth becomes ready
  useEffect(() => {
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      setRefreshKey(prev => prev + 1);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  const fetchDroplets = async () => {
    // Don't try to fetch if Auth0 is still loading or user is not authenticated
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      throw new Error('Authentication required. Please wait for Auth0 to initialize.');
    }
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl("/instances"), {
        method: "GET",
        headers: headers,
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Authentication required');
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch instances: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.sizes || [];
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const loadDroplets = async () => {
      // Don't try to load if Auth0 is still loading or user is not authenticated
      if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        const data = await fetchDroplets();
        setDroplets(data);
      } catch (err) {
        console.error('Error fetching instances:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Only load droplets if Auth0 is ready and user is authenticated
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      loadDroplets();
    } else if (!auth.hasAuth0) {
      // If Auth0 is not configured, still try to load (might use API key)
      loadDroplets();
    }
  }, [refreshKey, auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  const filteredDroplets = (droplets) => {
    if (!searchTerm) return droplets;
    const term = searchTerm.toLowerCase();
    return droplets.filter(droplet =>
      droplet.slug?.toLowerCase().includes(term) ||
      droplet.description?.toLowerCase().includes(term) ||
      droplet.provider?.toLowerCase().includes(term) ||
      availabilityValue(droplet.available).includes(term)
    );
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Navbar>
            <Navbar.Brand>Droplets</Navbar.Brand>
          </Navbar>
        </Header>
        <Content>
          <div style={{ padding: '20px' }}>
            <div>Loading droplets...</div>
          </div>
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Navbar>
            <Navbar.Brand>Droplets</Navbar.Brand>
          </Navbar>
        </Header>
        <Content>
          <div style={{ padding: '20px' }}>
            <Message type="error" showIcon>
              {error}
            </Message>
          </div>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <Navbar style={{ display: 'flex', alignItems: 'center' }}>
          <Navbar.Brand>
            Droplets
          </Navbar.Brand>
          <div style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <InputGroup inside style={{ width: 300 }}>
              <Input
                placeholder="Search by ID, description, provider, or availability"
                value={searchTerm}
                onChange={setSearchTerm}
              />
              <InputGroup.Addon>
                <Search />
              </InputGroup.Addon>
            </InputGroup>
            <Button appearance="primary" onClick={() => setRefreshKey(prev => prev + 1)} loading={loading}>
              Refresh
            </Button>
          </div>
        </Navbar>
      </Header>
      <Content>
        <div style={{ padding: '20px' }}>
          <Panel>

            <Table
              data={filteredDroplets(droplets)}
              loading={loading}
              autoHeight
              height={400}
            >
              <Column flexGrow={1} minWidth={200}>
                <HeaderCell>ID</HeaderCell>
                <Cell>
                  {(rowData) => (
                    rowData.slug ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {rowData.slug}
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <CopyButton
                            text={rowData.slug}
                            size="sm"
                            successMessage="ID copied to clipboard"
                            errorMessage="Failed to copy ID to clipboard"
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>-</div>
                    )
                  )}
                </Cell>
              </Column>
              <Column width={120}>
                <HeaderCell>Provider</HeaderCell>
                <Cell>
                  {(rowData) => rowData.provider || '-'}
                </Cell>
              </Column>
              <Column flexGrow={1} minWidth={200}>
                <HeaderCell>Description</HeaderCell>
                <Cell>
                  {(rowData) => rowData.description || '-'}
                </Cell>
              </Column>
              <Column width={100}>
                <HeaderCell>vCPUs</HeaderCell>
                <Cell>
                  {(rowData) => rowData.vcpus || '-'}
                </Cell>
              </Column>
              <Column width={120}>
                <HeaderCell>Memory</HeaderCell>
                <Cell>
                  {(rowData) => formatMemory(rowData.memory_mb)}
                </Cell>
              </Column>
              <Column width={100}>
                <HeaderCell>Disk</HeaderCell>
                <Cell>
                  {(rowData) => rowData.disk_gb ? `${rowData.disk_gb} GB` : '-'}
                </Cell>
              </Column>
              <Column width={120}>
                <HeaderCell>Price/Hour</HeaderCell>
                <Cell>
                  {(rowData) => formatPrice(rowData.price_hourly)}
                </Cell>
              </Column>
              <Column width={120}>
                <HeaderCell>Price/Month</HeaderCell>
                <Cell>
                  {(rowData) => formatPrice(rowData.price_monthly)}
                </Cell>
              </Column>
              <Column width={110}>
                <HeaderCell>Available</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const a = availabilityValue(rowData.available);
                    if (a === 'maybe') return <Tag color="orange">Maybe</Tag>;
                    if (a === 'yes') return <Tag color="green">Yes</Tag>;
                    return <Tag color="red">No</Tag>;
                  }}
                </Cell>
              </Column>
              <Column width={110}>
                <HeaderCell>Actions</HeaderCell>
                <Cell style={{ padding: '6px' }}>
                  {(rowData) => (
                    <Button
                      size="xs"
                      appearance="ghost"
                      onClick={() => setDetailsInstance(rowData)}
                    >
                      Details
                    </Button>
                  )}
                </Cell>
              </Column>
            </Table>
          </Panel>
        </div>
        <InstanceDetailsDialog
          show={!!detailsInstance}
          instance={detailsInstance}
          onClose={() => setDetailsInstance(null)}
        />
      </Content>
    </Container>
  );
}

export default GPUInstances;


